<app-side-dialog
  #dialog
  [headerText]="(isEdit ? 'Edit ' : 'New ') + resourceDisplayName"
  [saveText]="isEdit ? 'Save' : 'Create'"
  [headerGlyph]="resourceName | resourceGlyph"
  [busy]="loading"
  [body]="body"
  [validationFn]="validateForm.bind(this)"
  (save)="onSubmit()"
  [contentVisible]="!loading"
  [errorText]="isEdit ? 'Cannot edit resource' : 'Cannot create resource'"
  [errorMsg]="errorMsg"
></app-side-dialog>

<ng-template #body>
  <form [formGroup]="form" autocomplete="off" style="margin-top: 1rem">
    @if (!isServiceComboVisible() && !Utils.isPrivateCloud) {
      <div>
        Go to
        <a
          fd-link
          target="_blank"
          [href]="Const.RESOURCE_REF_URL[resourceName]"
          >{{ resourceDisplayName }}</a
        >
        in the Unified Services Framework - Reference Guide to see details about
        this resource's definition.
      </div>
    }

    @if (isServiceComboVisible() && !isEdit) {
      <div>
        Use this form to instantiate a resource type. @if (!Utils.isPrivateCloud) {To learn more, go to
        <a
          fd-link
          target="_blank"
          [href]="Const.RESOURCE_REF_URL[ResourceType.SERVICE_INSTANCE_GENERIC]"
          >Instantiating Resource Types</a
        >.}
      </div>
      @if (!Utils.isPrivateCloud) {
        <div style="padding-top: 0.5rem">
          For details about the configuration of the resource types provided by
          Unified Service, please refer to the
          <a
            fd-link
            target="_blank"
            href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/"
            >Reference Guide</a
          >.
        </div>
      }
    }

    @if (isServiceComboVisible() && isEdit) {
      <div>Use this form to maintain the editable fields of this resource.</div>
      @if (!Utils.isPrivateCloud) {
        <div style="padding-top: 0.5rem">
          For details about the configuration of the resource types provided by
          Unified Service, please refer to the
          <a
            fd-link
            target="_blank"
            href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/"
            >Reference Guide</a
          >.
        </div>
      }
    }

    <app-constraint-notification
      [resource]="selectedResource"
      [type]="constraintType"
    ></app-constraint-notification>

    @if (isServiceComboVisible()) {
      <div fd-form-item style="padding-top: 0.5rem">
        <label fd-form-label for="service-combobox" [required]="true"
          >Resource Type</label
        >
        <fd-combobox
          placeholder="Select a resource type"
          formControlName="managedService"
          id="service-combobox"
          inputId="service-combobox-input"
          [communicateByObject]="true"
          class="combobox-wide"
          [dropdownValues]="serviceInstancesByGroup"
          [displayFn]="displayServiceFunc"
          [itemTemplate]="columnTemplate"
          [state]="resourceCreationTypeState"
        >
        </fd-combobox>
        @if (resourceCreationTypeState === "error") {
          <fd-form-message type="error">
            You're not authorized to create this resource
          </fd-form-message>
        }
        <ng-template #columnTemplate let-item>
          <span class="combo-primary" fd-list-title>{{ item.type }}</span>
          <span
            class="combo-secondary"
            fd-list-secondary
            [title]="item.group"
            >{{ item.group }}</span
          >
        </ng-template>
      </div>
    }

    <fd-busy-indicator
      class="loading"
      size="m"
      [block]="true"
      [loading]="isPoliciesLoading()"
      ariaLabel="loading"
    >
    </fd-busy-indicator>

    @if (!isPoliciesLoading()) {
      <div style="margin-top: 1rem">
        <app-panel-expandable
          text="API Version"
          [body]="apiVersion"
          [description]="apiVersionInfo"
        >
        </app-panel-expandable>
        <ng-template #apiVersion>
          <app-container [body]="apiVersionBody"></app-container>
          <ng-template #apiVersionBody>
            <div class="api-version-wrapper">
              <div>
                <app-helpful-label
                  [required]="true"
                  for="group-name-input"
                  i18n-label="
                    Group Label|Label of the Group
                    input@@create.instance.group.input.label"
                  i18n-popoverTitle="
                    XTIT|Title of the Group
                    popover@@create.instance.group.popover.title"
                  label="Group"
                  popoverTitle="Group"
                  [popoverBody]="isEdit ? groupPopoverEdit : groupPopover"
                >
                </app-helpful-label>
                <ng-template
                  #groupPopover
                  i18n="
                    XMSG|Body text of the Group
                    popover@@create.instance.group.popover.body"
                >
                  Represents a group of resources or APIs that have some logical
                  relationship.
                  <br /><br />
                  For example, all system resources are defined under the group:
                  system.resource.api.sap.
                </ng-template>
                <ng-template
                  #groupPopoverEdit
                  i18n="
                    XMSG|Body text of the Group
                    popover@@create.instance.group.popover.body"
                >
                  Represents a group of resources or APIs that have some logical
                  relationship. Cannot be changed.
                </ng-template>
                <input
                  fd-form-control
                  fdCompact
                  type="text"
                  name="group"
                  id="group-input"
                  placeholder="Select a group"
                  formControlName="group"
                  class="name-input"
                />
              </div>
              <div class="version">
                <app-helpful-label
                  [required]="true"
                  for="version-combobox"
                  i18n-label="
                    Group Label|Label of the Version
                    input@@create.instance.version.input.label"
                  i18n-popoverTitle="
                    XTIT|Title of the Version
                    popover@@create.instance.version.popover.title"
                  label="Version"
                  popoverTitle="Version"
                  [popoverBody]="isEdit ? versionPopoverEdit : versionPopover"
                >
                </app-helpful-label>
                <ng-template
                  #versionPopover
                  i18n="
                    XMSG|Body text of the Version
                    popover@@create.instance.version.popover.body"
                >
                  Represents the specific version of the resource and its API.
                  <br /><br />
                  Example: v1
                </ng-template>
                <ng-template
                  #versionPopoverEdit
                  i18n="
                    XMSG|Body text of the Version
                    popover@@create.instance.version.popover.body"
                >
                  Represents the version of the resource and its API. Cannot be
                  changed.
                </ng-template>
                <fd-combobox
                  placeholder="Select a version"
                  formControlName="version"
                  id="version-combobox"
                  [communicateByObject]="true"
                  [dropdownValues]="form.get('managedService').value?.versions"
                  [displayFn]="displayVersionFunc"
                  (itemClicked)="onVersionChange($event)"
                  class="combobox-wide"
                  inputId="version-combobox-input"
                >
                </fd-combobox>
              </div>
            </div>
          </ng-template>
        </ng-template>
        <ng-template #apiVersionInfo>
          In a resource's configuration, the apiVersion is the concatenation of
          the group and the version, and is one of the required fields of a
          resource.
          <br /><br />Example: system.resource.api.sap/v1
        </ng-template>
      </div>
      @if (form.get("version").value) {
        <div style="margin-top: 1rem">
          <app-panel-expandable
            text="Metadata"
            [body]="metadata"
            [description]="metadataInfo"
          ></app-panel-expandable>
          <ng-template #metadata>
            <!--<h3 class="section-title" style="margin-bottom:1.5rem">Metadata</h3>-->
            <fd-form-input-message-group
              [triggers]="['mouseenter', 'mouseleave']"
            >
              <app-container [body]="nameAndPath"></app-container>
              <ng-template #nameAndPath>
                <app-helpful-label
                  [required]="true"
                  for="instance-name-input"
                  i18n-label="
                    Input Label|Label of the Technical Name
                    input@@create.technicalName.input.label"
                  i18n-popoverTitle="
                    XTIT|Title of the Technical Name
                    popover@@create.technicalName.popover.title"
                  label="Name"
                  popoverTitle="Name"
                  [popoverBody]="
                    isEdit ? technicalNamePopoverEdit : technicalNamePopover
                  "
                >
                </app-helpful-label>
                <ng-template
                  #technicalNamePopover
                  i18n="
                    XMSG|Body text of the Technical Name
                    popover@@create.technicalName.popover.body"
                >
                  The technical name of your resource.<br /><br />
                  The name must:
                  <ul>
                    <li>
                      Be DNS-compliant. It can contain only lowercase
                      alphabetical characters (a-z), numeric characters (0-9),
                      hyphens (-), and periods (.).
                    </li>
                    <li>Start and end with an alphanumeric character.</li>
                  </ul>
                  Once you've created the resource, the technical name cannot be
                  changed.<br /><br />
                  Examples: test-service.alias, resource-group-1,
                  binding.my-admin-role
                </ng-template>
                <ng-template
                  #technicalNamePopoverEdit
                  i18n="
                    XMSG|Body text of the Technical Name
                    popover@@create.technicalName.popover.body"
                >
                  The technical name of your resource. Cannot be changed.
                </ng-template>
                <input
                  fd-form-control
                  fdCompact
                  type="text"
                  name="name"
                  id="instance-name-input"
                  placeholder="Enter a name"
                  formControlName="name"
                  [disableControl]="isEdit"
                  [state]="Utils.getValidationState(form.get('name'))"
                  class="name-input"
                />
                <div style="margin-top: 1.5rem">
                  <app-helpful-label
                    for="path-input"
                    i18n-label="
                      Group Label|Label of the Path
                      input@@create.instance.path.input.label"
                    i18n-popoverTitle="
                      XTIT|Title of the Group
                      popover@@create.instance.path.popover.title"
                    label="Path"
                    popoverTitle="Path"
                    [popoverBody]="isEdit ? pathPopoverEdit : pathPopover"
                  >
                  </app-helpful-label>
                  <ng-template
                    #pathPopover
                    i18n="
                      XMSG|Body text of the Path
                      popover@@create.instance.path.popover.body"
                  >
                    The current level in the account hierarchy where the
                    resource will be created.
                  </ng-template>
                  <ng-template
                    #pathPopoverEdit
                    i18n="
                      XMSG|Body text of the Path
                      popover@@create.instance.path.popover.body"
                  >
                    The current level in the account hierarchy where the
                    resource resides.
                  </ng-template>
                  <input
                    fd-form-control
                    fdCompact
                    type="text"
                    name="path"
                    id="path-input"
                    formControlName="path"
                    class="name-input"
                  />
                </div>
                <div style="margin-top: 1.5rem">
                  <app-add-labels-and-annotations
                    #addLabelsAndAnnotationsComponent
                    [labelsExpanded]="!isLabelsEmpty(labels)"
                    [annotationsExpanded]="!isLabelsEmpty(annotations)"
                    [fixed]="false"
                    [labels]="labels"
                    [annotations]="annotations"
                    [isLabelsEnabled]="isLabelsEnabled"
                    [isAnnotationsEnabled]="isAnnotationEnabled"
                  ></app-add-labels-and-annotations>
                </div>
              </ng-template>
            </fd-form-input-message-group>
          </ng-template>
          <ng-template #metadataInfo>
            Data that helps to identify the resource.
          </ng-template>
        </div>
      }
      @if (form.get("version").value) {
        <div style="margin-top: 1rem; margin-bottom: 1rem">
          <formly-form
            [(model)]="model"
            [fields]="fields"
            [options]="options"
            [form]="formlyForm"
          ></formly-form>
        </div>
      }
    }
  </form>
</ng-template>
